import type { ErrorResponse, RefreshResponse } from "@shared/types";
import { notification } from "antd";
import axios, { type AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "@/modules/auth/store/auth.store";
import { categorizeError, getUserErrorMessage } from "@/shared/utils/error.utils";
import { authEndpoints } from "./endpoints";

export const apiClient = axios.create({
	baseURL: import.meta.env.VITE_API_URL || "",
	headers: {
		"Content-Type": "application/json",
	},
	timeout: 30000,
});

// ─── Request Interceptor ────────────────────────────────────────────
// Har bir so'rovga accessToken ni avtomatik qo'shish
apiClient.interceptors.request.use(
	(config: InternalAxiosRequestConfig) => {
		const accessToken = useAuthStore.getState().accessToken;
		if (accessToken) {
			config.headers.Authorization = `Bearer ${accessToken}`;
		}
		return config;
	},
	(error) => Promise.reject(error)
);

// ─── Response Interceptor ───────────────────────────────────────────
// 401 bo'lganda token refresh + concurrent request queue

type FailedRequest = {
	resolve: (token: string) => void;
	reject: (error: unknown) => void;
};

let isRefreshing = false;
let failedQueue: FailedRequest[] = [];

/**
 * Navbatdagi barcha so'rovlarni qayta ishlash
 * Agar token yangilangan bo'lsa — har biriga yangi token bilan resolve
 * Agar xato bo'lsa — har birini reject
 */
function processQueue(error: unknown, token: string | null = null): void {
	for (const request of failedQueue) {
		if (error) {
			request.reject(error);
		} else if (token) {
			request.resolve(token);
		}
	}
	failedQueue = [];
}

/**
 * Global error handler - barcha so'rovlar uchun umumiy error handling
 */
function handleGlobalError(error: AxiosError<ErrorResponse>): Promise<never> {
	const category = categorizeError(error);

	// 401 handled by token refresh interceptor - skip here
	if (category === "authentication") {
		return Promise.reject(error);
	}

	const { message, description } = getUserErrorMessage(error);

	switch (category) {
		case "forbidden":
			// 403 - Foydalanuvchida ruxsat yo'q
			notification.error({
				message: "Ruxsat yo'q",
				description: message,
				duration: 6,
			});
			break;

		case "server":
			// 500+ - Server xatolari
			notification.error({
				message: "Server xatoligi",
				description: description || message,
				duration: 8,
			});
			break;

		case "network":
		case "timeout":
			// Network xatolari
			notification.error({
				message,
				description,
				duration: 6,
			});
			break;

		default:
			// Boshqa xatolar - faqat log qilish, reject qilish
			break;
	}

	return Promise.reject(error);
}

apiClient.interceptors.response.use(
	(response: AxiosResponse) => response,
	async (error: AxiosError<ErrorResponse>) => {
		const originalRequest = error.config as InternalAxiosRequestConfig & {
			_retry?: boolean;
		};

		// Faqat 401 va birinchi urinish bo'lgandagina refresh qilamiz
		if (error.response?.status !== 401 || originalRequest._retry) {
			// Global error handler - boshqa barcha xatolar uchun
			return handleGlobalError(error);
		}

		// Login/logout — 401 bu "noto'g'ri parol" yoki "invalid", refresh kerak emas
		if (
			originalRequest.url?.includes("/auth/login") ||
			originalRequest.url?.includes("/auth/logout")
		) {
			return Promise.reject(error);
		}

		// Refresh endpoint'ning o'zi 401 bersa — loop'dan saqlanish
		if (originalRequest.url?.includes("/auth/refresh")) {
			useAuthStore.getState().logout();
			return Promise.reject(error);
		}

		originalRequest._retry = true;

		// Agar allaqachon refresh jarayonida bo'lsak — navbatga qo'shamiz
		if (isRefreshing) {
			return new Promise<string>((resolve, reject) => {
				failedQueue.push({ resolve, reject });
			}).then((newToken) => {
				originalRequest.headers.Authorization = `Bearer ${newToken}`;
				return apiClient(originalRequest);
			});
		}

		isRefreshing = true;

		const refreshToken = useAuthStore.getState().refreshToken;

		if (!refreshToken) {
			isRefreshing = false;
			useAuthStore.getState().logout();
			return Promise.reject(error);
		}

		try {
			// Refresh uchun alohida axios instance ishlatamiz (interceptor loop'dan himoya)
			const refreshURL = `${import.meta.env.VITE_API_URL || ""}${authEndpoints.refresh}`;
			const response = await axios.post<RefreshResponse>(refreshURL, { refreshToken });

			const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data.data;

			// Store'ni yangilash (faqat tokenlarni, user.organizations ni yo'qotmaslik uchun)
			useAuthStore.getState().setTokens(newAccessToken, newRefreshToken);

			// Navbatdagi barcha so'rovlarga yangi token berish
			processQueue(null, newAccessToken);

			// Original so'rovni yangi token bilan qaytadan yuborish
			originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
			return apiClient(originalRequest);
		} catch (refreshError) {
			// Refresh muvaffaqiyatsiz — barcha navbatni reject qilish va logout
			// ProtectedRoute isAuthenticated: false ni ko'rib /login ga yo'naltiradi
			processQueue(refreshError, null);
			useAuthStore.getState().logout();
			return Promise.reject(refreshError);
		} finally {
			isRefreshing = false;
		}
	}
);

export default apiClient;
